/**
 * /documents — Documents workspace. Surface Layer (/01)
 *
 * Lists document-generation jobs from `GET /api/documents` (typed
 * documents_jobs ledger) and enqueues new ones as `documents.generate`
 * ActionFrames. Job outputs land once the execution worker writes them;
 * until then jobs render their `queued`/`running`/`succeeded` status.
 */

import { useEffect, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Button,
  Column,
  Grid,
  InlineNotification,
  SkeletonText,
  Tag,
  TextInput,
  Tile,
  preview__IconIndicator as IconIndicator,
} from '@carbon/react';
import { Document } from '@carbon/icons-react';
import type { DocumentRow } from '@contracts/runtime-endpoints.contract';
import { useDocuments } from '@lib/persistence/useRuntimeData';
import { useActiveProjectStore } from '@lib/persistence/useActiveProject';
import { useDispatchStore } from '../rebuilt/action-dispatch';
import '../styles/views.scss';

export const Route = createFileRoute('/documents')({
  component: DocumentsView,
});

type JobStatus = DocumentRow['status'];

// Carbon status-indicator kinds (verified against the installed @carbon/react
// IconIndicator: failed|caution-*|succeeded|in-progress|pending|incomplete|…).
const STATUS_KIND: Record<JobStatus, string> = {
  succeeded: 'succeeded',
  running: 'in-progress',
  queued: 'pending',
  requires_approval: 'caution-minor',
  failed: 'failed',
  cancelled: 'incomplete',
};

function DocumentsView() {
  const dispatch = useDispatchStore((s) => s.dispatch);
  const active = useActiveProjectStore((s) => s.active);
  const { documents, isLoading, tenantContextMissing, refetch } = useDocuments(undefined, active?.id);
  const [topic, setTopic] = useState('');
  const [result, setResult] = useState<{ status: string; detail: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const refetchTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (refetchTimerRef.current !== null) window.clearTimeout(refetchTimerRef.current);
    },
    []
  );

  async function enqueue() {
    setBusy(true);
    const entry = await dispatch(
      'documents.generate',
      {
        topic: topic.trim() || 'Untitled document',
        format: 'markdown',
        ...(active ? { project_id: active.id } : {}),
      },
      { label: 'Generate document' }
    );
    setResult({ status: entry.status, detail: entry.detail });
    // The worker writes the row asynchronously; refetch shortly after enqueue.
    if (entry.status !== 'error') {
      refetchTimerRef.current = window.setTimeout(() => refetch(), 1200);
    }
    setBusy(false);
  }

  return (
    <div className="view">
      <header className="view__header">
        <h1 className="view__title">Documents</h1>
        <p className="view__subtitle">
          Generate, review and export documents. Generation is an approval-aware ActionFrame.
        </p>
      </header>

      <div className="surface-grid">
        <Grid condensed>
          <Column sm={4} md={8} lg={16}>
            <Tile className="view-tile">
              <h2 className="view-tile__value">New document</h2>
              <TextInput
                id="documents-topic"
                labelText="Topic"
                placeholder="e.g. Q3 launch plan"
                value={topic}
                onChange={(event) => setTopic(event.currentTarget.value)}
              />
              <div className="view-actions">
                <Button renderIcon={Document} disabled={busy} onClick={() => void enqueue()}>
                  Generate
                </Button>
              </div>
              {result ? (
                <InlineNotification
                  kind={result.status === 'error' || result.status === 'denied' ? 'error' : 'info'}
                  lowContrast
                  hideCloseButton
                  title={result.status === 'error' || result.status === 'denied' ? 'Dispatch failed' : 'Enqueued'}
                  subtitle={result.detail}
                />
              ) : null}
            </Tile>
          </Column>

          {tenantContextMissing ? (
            <Column sm={4} md={8} lg={16}>
              <InlineNotification
                kind="info"
                lowContrast
                hideCloseButton
                title="Workspace being provisioned"
                subtitle="Documents appear once your workspace context is ready."
              />
            </Column>
          ) : isLoading && documents.length === 0 ? (
            <Column sm={4} md={8} lg={16}>
              <SkeletonText paragraph lineCount={4} />
            </Column>
          ) : documents.length === 0 ? (
            <Column sm={4} md={8} lg={16}>
              <Tile className="view-empty">
                <h2>No documents yet</h2>
                <p className="view-tile__label">Generate a document above to populate this workspace.</p>
              </Tile>
            </Column>
          ) : (
            documents.map((doc) => (
              <Column key={doc.id} sm={4} md={4} lg={4}>
                <Tile className="view-tile">
                  <div className="view-tile__row">
                    <strong>{doc.title}</strong>
                    <IconIndicator kind={STATUS_KIND[doc.status] ?? 'unknown'} label={doc.status} size={16} />
                  </div>
                  <div className="notification-row__meta">
                    <span>{doc.doc_type}</span>
                    <Tag type="outline">{doc.format}</Tag>
                    {doc.output_uri ? <Tag type="teal">output ready</Tag> : null}
                  </div>
                  {doc.error ? <p className="view-tile__label">Error: {doc.error}</p> : null}
                </Tile>
              </Column>
            ))
          )}
        </Grid>
      </div>
    </div>
  );
}
